import { useMemo, useState } from 'react';

import { Drawer } from '@equip.health/ui';
import { useHistory, useLocation } from 'react-router-dom';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import EditInquiry from '~/components/common/inquiry/drawer/EditInquiry';
import InquiryDrawerHeader from '~/components/common/inquiry/drawer/InquiryDrawerHeader';
import {
  EditMode,
  InquiryValues,
} from '~/components/common/inquiry/drawer/types';
import { useEditInquiry } from '~/components/common/inquiry/drawer/useEditInquiry';
import { useShowRequiredFieldErrors } from '~/components/common/inquiry/drawer/useShowRequiredFieldErrors';
import {
  createInquiryRequest,
  createNoteRequest,
  isMissingFields,
} from '~/components/common/inquiry/drawer/util';
import ReviewPatientMatchesModal from '~/components/maud/inquiries/InquiryDetail/ReviewPatientMatchesModal';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { OTHER_PAYOR } from '~/lib/constants/inquiry.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useApi } from '~/lib/hooks';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { getAllPatientMatches } from '~/lib/util/patient.util';
import { Path } from '~/routes/constants';
import { useInquiryListDispatch } from '~/store/reducers/inquiryList';
import { useAddInquiryMutation } from '~/store/services/inquiry';
import { useCreateNoteMutation } from '~/store/services/notes/notes';
import { editorV2ContentHasText } from '~/lib/util/editorV2ContentHasText';

const TOP_LEVEL_ID = 'create-inquiry';

const CreateInquiryDrawer = () => {
  const [isOpenExitConfirm, setIsOpenExitConfirm] = useState<boolean>(false);
  const { showRequiredFieldErrors, setShowRequiredFieldErrors } =
    useShowRequiredFieldErrors();
  const useEditInquiryState = useEditInquiry();
  const { showErrorToast } = useErrorToast();

  const { reloadInquiryList } = useInquiryListDispatch();
  const { createInquiryDrawer, setComponentVisibility } =
    useComponentPortalContext();

  const { loading: createNoteLoading } = useApi<{
    noteExternalId: string;
  }>();

  // TODO: Commenting out temporarily for https://teamequip.atlassian.net/browse/PT-894.
  /*
  const {
    isOpen: isReviewPatientMatchesModalOpen,
    onClose: onReviewPatientMatchesModalClose,
    onOpen: onReviewPatientMatchesModalOpen,
  } = useDisclosure();

  const [
    getPatientMatches,
    { data: patientMatches, isLoading: isPatientMatchesLoading },
  ] = useLazyGetPatientMatchesQuery();
   */
  const isReviewPatientMatchesModalOpen = false;
  const patientMatches: GetPatientMatches = null;
  const isPatientMatchesLoading = false;
  const onReviewPatientMatchesModalClose = () => { };

  const [createNote] = useCreateNoteMutation();
  const [createInquiry, { isLoading: createInquiryLoading }] =
    useAddInquiryMutation();

  const { currentUser } = useGlobalContext();

  const { push } = useHistory();
  const { pathname } = useLocation();

  const inquiryValue: InquiryValues = {
    contacts: useEditInquiryState.contacts,
    coverages: useEditInquiryState.coverage,
    info: useEditInquiryState.info,
    note: useEditInquiryState.note,
    patient: useEditInquiryState.patient,
    referral: useEditInquiryState.referral,
  };
  const confirmCloseDrawer = () => {
    useEditInquiryState.resetState();
    setShowRequiredFieldErrors(false);
    setIsOpenExitConfirm(false);
    setComponentVisibility({
      component: PortalComponent.CREATE_INQUIRY_DRAWER,
      isVisible: false,
    });
  };

  const handleCloseInquiryDetailDrawer = () => {
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: null,
      isVisible: false,
    });
  };

  const finalizeCreate = (inquiryId: string) => {
    confirmCloseDrawer();
    reloadInquiryList();
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: {
        inquiryId,
        onClose: handleCloseInquiryDetailDrawer,
      },
      isVisible: true,
    });
    if (pathname === Path.MAUD_INQUIRIES) {
      push(`?id=${inquiryId}`);
    }
  };

  const onSaveNote = (noteValue: PersistNoteRequest) => {
    createNote(noteValue)
      .unwrap()
      .then(() => {
        finalizeCreate(noteValue.inquiryId);
      })
      .catch((error) => {
        showErrorToast(GlobalErrorCode.CREATE_INQUIRY_V2, error.status);
      });
  };

  const handleCreateInquiry = (patientId?: string) => {
    createInquiry(
      createInquiryRequest({
        ...inquiryValue,
        coverage: {
          ...inquiryValue.coverages,
          customPayorName:
            inquiryValue.coverages.payor === OTHER_PAYOR
              ? inquiryValue.coverages.customPayorName
              : undefined,
        },
        patient: {
          ...inquiryValue.patient,
          id: patientId || inquiryValue.patient.id,
        },
      }),
    )
      .unwrap()
      .then(({ id: inquiryId, patient: { id: patientId } }) => {
        if (editorV2ContentHasText(inquiryValue.note.content)) {
          onSaveNote(
            createNoteRequest(
              inquiryId,
              currentUser.userId,
              patientId,
              inquiryValue.note,
            ),
          );
        } else {
          finalizeCreate(inquiryId);
        }
      })
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.CREATE_INQUIRY_V2,
          error.status,
          error.data,
        );
      });
  };

  const onSave = () => {
    if (!isMissingFields(inquiryValue)) {
      handleCreateInquiry();
      // TODO: Commenting out temporarily for https://teamequip.atlassian.net/browse/PT-894.
      /*
      const primaryContact = inquiryValue.contacts.find(
        (c) => c.isPrimaryContact,
      );

      // Convert date string from format MM/DD/YYYY to YYYY-MM-DD
      const dateOfBirth = inquiryValue.patient.dateOfBirth
        ? DateTime.fromJSDate(
            new Date(inquiryValue.patient.dateOfBirth),
          ).toISODate()
        : null;

      getPatientMatches({
        contactEmail: primaryContact?.email,
        contactPhoneNumber: primaryContact?.firstName,
        patientDateOfBirth: dateOfBirth,
        patientEmail: inquiryValue.patient.email,
        patientFirstName: inquiryValue.patient.firstName,
        patientLastName: inquiryValue.patient.lastName,
        patientPhoneNumber: inquiryValue.patient.phoneNumber,
        relationWithPatient: primaryContact?.relationToPatient,
        supportFirstName: primaryContact?.firstName,
        supportLastName: primaryContact?.lastName,
      })
        .unwrap()
        .then((matches) => {
          const allMatches = getAllPatientMatches(matches);

          if (isEmpty(allMatches)) {
            handleCreateInquiry();
          } else {
            onReviewPatientMatchesModalOpen();
          }
        });
       */
    } else {
      setShowRequiredFieldErrors(true);
    }
  };

  const handleConfirmPatient = (patientId: string) => {
    handleCreateInquiry(patientId);
  };

  const isLoading =
    createInquiryLoading || createNoteLoading || isPatientMatchesLoading;

  const allPatientMatches = useMemo(() => {
    return getAllPatientMatches(patientMatches);
  }, [patientMatches]);

  return (
    <Drawer
      header={
        <InquiryDrawerHeader
          isLoading={isLoading}
          onSave={onSave}
          title="New Inquiry"
        />
      }
      id={TOP_LEVEL_ID}
      isOpen={createInquiryDrawer.isVisible}
      onClose={() => setIsOpenExitConfirm(true)}
    >
      <EditInquiry
        editInquiryState={useEditInquiryState}
        editMode={EditMode.CREATE}
        showRequiredFieldErrors={showRequiredFieldErrors}
      />
      <DiscardChangesModalV2
        isOpen={isOpenExitConfirm}
        onClose={() => setIsOpenExitConfirm(false)}
        onConfirm={confirmCloseDrawer}
      />
      {allPatientMatches.length > 0 && (
        <ReviewPatientMatchesModal
          id={`${TOP_LEVEL_ID}__review-patient-matches-modal`}
          isOpen={isReviewPatientMatchesModalOpen}
          onCancel={onReviewPatientMatchesModalClose}
          onConfirmPatient={handleConfirmPatient}
          onCreatePatient={handleCreateInquiry}
          patientMatches={allPatientMatches}
        />
      )}
    </Drawer>
  );
};

export default CreateInquiryDrawer;